import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import { Cart as ICart } from 'shared-components/src/page-builder/interface/checkoutInterface';
import SearchFilter from 'components/common/searchFilter';
import { SearchProps } from 'components/transactions';
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks';
import { IOrderTableFilter } from 'interface/orderInterface';
import { ReactNode, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import orderService from 'services/orderService';
import {
  formattedDate,
  getTotalPages,
  iterateHeadCellKeys,
  setPaginationCommon,
} from 'utils/helper';
import { Loader } from 'components/common/loader';
import { get } from 'lodash';
import { setCurrentFilter } from 'redux-setup/slices/pageConfigSlice';
import SideFilter from 'components/abandonedCarts/abandonedCartFilter/sideFilter';
import Cart from 'components/abandonedCarts/cart';
import {
  ICartsPayloadBody,
  ICartsSideFilter,
} from 'interface/processorAssignment';
import StatusBadge from 'components/common/statusBadge';
import globalOrderService from 'services/orderService';
import dayjs from 'dayjs';
import PaginationCommon from 'components/common/PaginationCommon';

export interface HeadCell {
  id: string;
  label: string;
  key: string;
  cellRender?: (_row: ICart) => ReactNode;
  hide?: boolean;
  showSortIcon?: boolean;
  showInSearch?: boolean;
  searchFiedtType?: 'input' | 'select';
  searchFieldOptions?: { label: string; value: string }[];
  inputType?: string;
}

export const defaultCartFilterValues: ICartsSideFilter = {
  Emails: [],
  MaxTotalAmount: 0,
  MinTotalAmount: 0,
  Status: [],
  TrafficChannels: [],
  IncludeNoContactInfo: false,
  StartDate: null,
  EndDate: null,
  StartModifiedDate: null,
  EndModifiedDate: null,
};

const AbandonedCarts = () => {
  const dispatch = useAppDispatch();
  const [search, setSearchValue] = useState<SearchProps>({});
  const [totalPage, settotalPage] = useState<number>(0);
  const { orderColumnFilter } = useAppSelector(state => state.coloumnFilter);
  const { storeIds } = useAppSelector(state => state.storeIds);
  const storefrontIds = useAppSelector(state => {
    const arr: string[] = [];
    state.storeIds?.storeIds?.forEach(val => {
      val?.storeFronts?.map(val => {
        arr.push(val?.ID);
      });
    });
    return arr;
  });
  const [loading, setLoading] = useState(0);
  const [cartList, setCartList] = useState<ICart[]>([]);
  const [selected, setSelected] = useState<Map<string, ICart>>(new Map());
  const [searchParams, setSearchParams] = useSearchParams();
  const rowsPerPage = 25;
  const [limit, setLimit] = useState<number>(
    searchParams.get('Limit')
      ? Number(searchParams.get('Limit')) || rowsPerPage
      : rowsPerPage,
  );
  const { timeZone } = useAppSelector(state => state.pathConfig);
  const [totalValueForPagination, setTotalValueForPagination] =
    useState<number>(4);
  const [isMoreData, setIsMoreData] = useState<boolean>(false);
  const [active, setActive] = useState<number>(1);
  const [isLastPage, setIsLastPage] = useState(false);

  const [paginationData, setPaginationData] = useState<Map<number, ICart[]>>(
    new Map(),
  );

  const headCells: readonly HeadCell[] = [
    {
      id: 'UpdatedAt',
      label: 'Last Modified',
      key: 'UpdatedAt',
      cellRender: row =>
        row?.UpdatedAt ? (
          <Tooltip placement="top" title={formattedDate(row?.UpdatedAt)} arrow>
            <span>{formattedDate(row?.UpdatedAt, true)}</span>
          </Tooltip>
        ) : (
          '-'
        ),
      hide: false,
      showInSearch: true,
      showSortIcon: true,
    },
    {
      id: 'Store.Name',
      label: 'Store Name',
      key: 'StoreName',
      hide: false,
      showInSearch: true,
      showSortIcon: true,
      cellRender: row => row?.Store?.Name ?? '-',
    },
    {
      id: 'Channel.Name',
      label: 'Channel Name',
      key: 'ChannelName',
      hide: false,
      showInSearch: true,
      showSortIcon: true,
      cellRender: row => row?.Channel?.Name ?? '-',
    },
    {
      id: 'Email',
      label: 'Email',
      key: 'Email',
      hide: false,
      showInSearch: true,
      showSortIcon: true,
    },
    {
      id: 'PhoneNumber',
      label: 'Phone Number',
      key: 'PhoneNumber',
      hide: false,
      showInSearch: true,
      showSortIcon: true,
    },
    {
      id: 'IPAddress',
      label: 'IP Address',
      key: 'IPAddress',
      cellRender: row => row?.IPAddress ?? '-',
      hide: false,
      showInSearch: true,
      showSortIcon: true,
    },
    {
      id: 'HasSubscription',
      label: 'Has Subscription',
      key: 'HasSubscription',
      cellRender: row => (
        <span
          className={row.HasSubscription ? 'text-green-700' : 'text-red-700'}>
          {row.HasSubscription ? 'Yes' : 'No'}
        </span>
      ),
      hide: false,
      showInSearch: true,
      showSortIcon: true,
      searchFiedtType: 'select',
      searchFieldOptions: [
        { label: 'Yes', value: 'true' },
        { label: 'No', value: 'false' },
      ],
    },
    {
      id: 'Status',
      label: 'Status',
      key: 'Status',
      hide: false,
      cellRender: row => <StatusBadge status={row.Status} />,
      showInSearch: true,
      showSortIcon: true,
    },
    {
      id: 'LineItems',
      label: 'Cart',
      key: 'cart',
      cellRender: row => <Cart row={row} />,
      hide: false,
      showSortIcon: false,
    },
    {
      id: 'CostSummary.TotalAmount',
      label: 'Total',
      key: 'Total',
      cellRender: row => row?.CostSummary?.TotalAmount || '-',
      hide: false,
      showInSearch: false,
      showSortIcon: false,
    },
    {
      id: 'TrafficSource',
      label: 'Traffic',
      key: 'TrafficSource',
      hide: false,
      showInSearch: true,
      showSortIcon: true,
    },
  ];

  const [headCellList, setHeadCellList] = useState<HeadCell[]>([
    ...headCells.map(value => {
      return {
        ...value,
        hide: orderColumnFilter?.includes(value.label),
      };
    }),
  ]);
  const filteredField = iterateHeadCellKeys(headCellList);
  const [page, setPage] = useState<number>(
    Number(searchParams.get('page_count') || 1),
  );
  const [filter, setFilter] = useState<IOrderTableFilter>({
    Descending: searchParams.get('descending') === 'false' ? false : true,
    OrderBy: searchParams.get('order_by') || 'ID',
    Limit: limit,
  });
  const [sideFormFilter, setSideFormFilter] = useState<ICartsSideFilter>({
    Emails: searchParams?.get('emails')
      ? JSON.parse(searchParams.get('emails') || '')
      : [],
    MaxTotalAmount: searchParams.get('MaxTotalAmount')
      ? Number(searchParams.get('MaxTotalAmount'))
      : 0,
    MinTotalAmount: searchParams.get('MinTotalAmount')
      ? Number(searchParams.get('MinTotalAmount'))
      : 0,
    Status: searchParams.get('status')
      ? JSON.parse(searchParams.get('status') || '')
      : [],
    StoreIDs: searchParams.get('storeIDs')
      ? JSON.parse(searchParams.get('storeIDs') || '')
      : [],
    TrafficChannels: searchParams.get('TrafficChannels')
      ? JSON.parse(searchParams.get('TrafficChannels') || '')
      : [],
    IncludeNoContactInfo:
      searchParams.get('IncludeNoContactInfo') === 'true' ? true : false,
    StartDate: searchParams.get('StartDate')
      ? dayjs(searchParams.get('StartDate')).tz()
      : null,
    EndDate: searchParams.get('EndDate')
      ? dayjs(searchParams.get('EndDate')).tz()
      : null,
    StartModifiedDate: searchParams.get('StartModifiedDate')
      ? dayjs(searchParams.get('StartModifiedDate')).tz()
      : null,
    EndModifiedDate: searchParams.get('EndModifiedDate')
      ? dayjs(searchParams.get('EndModifiedDate')).tz()
      : null,
  });

  const sortHandler = (orderBy: string) => {
    setFilter(pre => {
      return {
        ...pre,
        OrderBy: orderBy,
        Descending: pre.OrderBy === orderBy ? !pre.Descending : true,
      };
    });
  };

  const handlePaginationValueChange = (val: number) => {
    setLimit(val);
    setTotalValueForPagination(getTotalPages(val));
  };

  const generatePayload = (isSetSearchParams = true) => {
    const {
      Emails,
      Status,
      MinTotalAmount,
      MaxTotalAmount,
      TrafficChannels,
      IncludeNoContactInfo,
      StartDate,
      EndDate,
      StartModifiedDate,
      EndModifiedDate,
    } = sideFormFilter;
    const { Descending, OrderBy } = filter;
    const payload: ICartsPayloadBody = {
      ...filter,
      Page: page - 1,
      Limit: limit,
    };
    if (isSetSearchParams) {
      setSearchParams(
        {
          status: Status ? JSON.stringify(Status) : '',
          Emails: Emails ? JSON.stringify(Emails) : '',
          TrafficChannels: TrafficChannels
            ? JSON.stringify(TrafficChannels)
            : '',
          IncludeNoContactInfo:
            IncludeNoContactInfo === true ? 'true' : 'false',
          StartDate: StartDate ? StartDate.tz().format() : '',
          EndDate: EndDate ? EndDate.tz().format() : '',
          StartModifiedDate: StartModifiedDate
            ? StartModifiedDate.tz().format()
            : '',
          EndModifiedDate: EndModifiedDate ? EndModifiedDate.tz().format() : '',
          MinTotalAmount: MinTotalAmount ? String(MinTotalAmount) : '',
          MaxTotalAmount: MaxTotalAmount ? String(MaxTotalAmount) : '',
          page_count: String(page),
          desending: String(Descending),
          order_by: OrderBy,
          Limit: String(limit),
        },
        { replace: true },
      );
    }
    payload.IncludeNoContactInfo = IncludeNoContactInfo;
    if (Status?.length) {
      payload.Status = Status;
    } else {
      payload.Status = ['pending', 'failed'];
    }
    if (Emails?.length) {
      payload.Emails = Emails;
    }
    if (MaxTotalAmount) {
      payload.MaxTotalAmount = Number(MaxTotalAmount);
    }
    if (MinTotalAmount) {
      payload.MinTotalAmount = Number(MinTotalAmount);
    }
    if (TrafficChannels?.length) {
      payload.TrafficChannels = TrafficChannels;
    }
    if (Object.values(search).length > 0) {
      payload.SearchFields = search;
      delete payload.Status;
    }
    if (storeIds?.length) {
      payload.StoreIDs = storeIds?.map(store => store.ID);
    }
    if (storefrontIds?.length) {
      payload.StorefrontIDs = storefrontIds;
    }
    if (StartDate) {
      payload.StartDate = StartDate.tz().startOf('day').format();
    }
    if (EndDate) {
      payload.EndDate = EndDate.tz().add(1, 'day').startOf('day').format();
    }
    if (StartModifiedDate) {
      payload.StartModifiedDate = StartModifiedDate.tz()
        .startOf('day')
        .format();
    }
    if (EndModifiedDate) {
      payload.EndModifiedDate = EndModifiedDate.tz()
        .add(1, 'day')
        .startOf('day')
        .format();
    }
    return payload;
  };

  const downloadCsvFile = async () => {
    setLoading(pre => pre + 1);
    const payload = generatePayload(false);
    payload.Page = 0;
    payload.Limit = totalPage || 25;
    const response = await globalOrderService.exportAbandonedCart(payload);
    if (response?.status === 200) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'abandonedCart.csv');
      document.body.appendChild(link);
      link.click();
    }
    setLoading(pre => pre - 1);
  };

  const searchCartList = async (details?: {
    currentPage?: number;
    reset?: boolean;
    isPrevious?: boolean;
  }) => {
    setLoading(pre => pre + 1);
    let page = 0;
    if (details?.isPrevious) {
      const currentPage = details?.currentPage ?? 0;
      page =
        currentPage % totalValueForPagination === 0
          ? currentPage / totalValueForPagination - 1
          : Math.floor(currentPage / totalValueForPagination);
    } else {
      page = details?.currentPage
        ? Math.floor(details?.currentPage / totalValueForPagination)
        : 0;
    }
    const payload = generatePayload();
    const res = await orderService.listCarts({
      ...payload,
      Page: page,
      Limit: limit * totalValueForPagination,
    });

    if (res?.status === 200) {
      if (res?.data?.Result?.length > 0) {
        if (details?.reset ?? true) {
          const data = res?.data?.Result.slice(0, limit);
          setCartList(data);
        }
        setPaginationCommon(
          res?.data?.Result,
          details?.currentPage || 1,
          details?.reset ?? true,
          limit,
          totalValueForPagination,
          setIsMoreData,
          setPaginationData,
          setActive,
          setIsLastPage,
          page,
        );
        settotalPage(res?.data?.TotalCount);
      } else {
        settotalPage(0);
        setCartList([]);
        setPaginationData(new Map());
      }
    } else {
      settotalPage(0);
      setCartList([]);
      setPaginationData(new Map());
    }
    setLoading(pre => pre - 1);
  };

  const filterSubmission = (formData: ICartsSideFilter) => {
    if (!loading) {
      setSideFormFilter(formData);
      setPage(1);
    }
  };

  useEffect(() => {
    searchCartList();
  }, [sideFormFilter, filter, limit, search, storeIds, timeZone]);

  useEffect(() => {
    page !== 1 && searchCartList({ reset: false, isPrevious: false });
  }, [page]);

  useEffect(() => {
    return () => {
      dispatch(setCurrentFilter(''));
    };
  }, []);

  return (
    <div className="orders_wrapper">
      <div className="order">
        <div className="order_search_container">
          <SearchFilter
            filteredField={filteredField}
            setSearchValue={data => {
              setPage(1);
              setSearchValue(data);
            }}
            searchValue={search}
          />
        </div>
        <TableContainer className="Common_Table">
          <Table
            className="orders_table"
            aria-labelledby="tableTitle"
            stickyHeader>
            <TableHead className="table_header">
              <TableRow>
                {headCellList.map(headCell => {
                  if (headCell.hide) {
                    return null;
                  }
                  return (
                    <TableCell className="table_header_cell" key={headCell.key}>
                      {headCell?.showSortIcon ? (
                        <TableSortLabel
                          className="header_text"
                          active={filter.OrderBy === headCell.id}
                          direction={
                            filter?.OrderBy === headCell.id
                              ? filter.Descending
                                ? 'desc'
                                : 'asc'
                              : 'asc'
                          }
                          onClick={() => sortHandler(headCell.id)}>
                          {headCell.label}
                        </TableSortLabel>
                      ) : (
                        headCell.label
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody className="table_body">
              {cartList?.length > 0 ? (
                cartList.map(row => {
                  const isItemSelected = !!selected.get(row.ID);
                  return (
                    <TableRow
                      hover
                      className="table_row"
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.ID}
                      selected={isItemSelected}>
                      {headCellList.map(headCell => {
                        if (headCell.hide) {
                          return null;
                        }
                        return (
                          <TableCell
                            className="table_cell"
                            key={headCell.label}
                            component="th"
                            id={headCell.key}
                            scope="row">
                            {headCell?.cellRender
                              ? headCell.cellRender(row)
                              : get(row, headCell.id)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              ) : (
                <div className="no-data-row">No data found</div>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <PaginationCommon
          isLastPage={isLastPage}
          setIsLastPage={setIsLastPage}
          active={active}
          setActive={setActive}
          isMoreData={isMoreData}
          totalValueForPagination={totalValueForPagination}
          limit={limit}
          paginationData={paginationData}
          setPaginationData={setPaginationData}
          searchApi={searchCartList}
          setData={setCartList}
          loading={!!loading}
          onRowsPerChange={val => {
            setSelected(new Map());
            handlePaginationValueChange(val);
          }}
        />
        <Loader loading={!!loading} />
      </div>
      <SideFilter
        headCellList={headCellList}
        setHeadCellList={setHeadCellList}
        sideFormFilter={sideFormFilter}
        filterSubmission={filterSubmission}
        loading={loading}
        exportList={downloadCsvFile}
      />
    </div>
  );
};

export default AbandonedCarts;
